/**
 * Deterministic GAME glucose physics.
 *
 * This is a deliberately simple "game physics" model (sigmoid carb absorption
 * vs. sigmoid insulin action + bounded noise) chosen so that the teaching
 * properties hold and the same choices produce the same battle:
 *
 *  - carbs raise glucose, insulin lowers it
 *  - pre-bolusing beats bolusing after the meal (lower peak)
 *  - fat slows absorption (later peak)
 *  - activity blunts carbs and amplifies insulin
 *  - a seeded "mystery factor" adds honest variance so kids learn that
 *    variance is normal and NOT personal failure
 *
 * It is NOT a medical model. Never use it for real decisions.
 */

export type ActivityLevel = 'rest' | 'active' | 'sports';

export interface MealSimulation {
  /** glucose at meal time, mg/dL */
  startGlucoseMgdl: number;
  /** ACTUAL carbs eaten (the world does not care what you scouted) */
  carbsGrams: number;
  /** 0 = fast carbs, 1 = hearty, 2 = double-cheese level armor */
  fatLevel: 0 | 1 | 2;
  /** insulin units given (0 allowed) */
  insulinUnits: number;
  /** minutes BEFORE the meal the bolus was given (negative = after eating) */
  preBolusMinutes: number;
  activity?: ActivityLevel;
  /** 0..1 honest-variance amplitude. 0 = fully deterministic. */
  mystery?: number;
  /** RNG seed for the mystery factor */
  seed?: number;
}

export interface GlucosePoint {
  /** minutes relative to the meal (t=0). Starts at -30. */
  tMinutes: number;
  glucoseMgdl: number;
}

const CARB_RISE_PER_G = 3.0; // mg/dL per gram at full absorption
const INSULIN_DROP_PER_U = 32; // mg/dL per unit at full action
const CARB_PEAK_MIN = 80;
const CARB_WIDTH_MIN = 25;
const INSULIN_PEAK_LAG_MIN = 75; // action midpoint after dose
const INSULIN_WIDTH_MIN = 30;
const FAT_PEAK_SHIFT_MIN = 25; // per fat level
const MIN_CLAMPMGDL = 45;
const MAX_CLAMP_MGDL = 400;

const ACTIVITY_MODS: Record<ActivityLevel, { carb: number; insulin: number }> = {
  rest: { carb: 1, insulin: 1 },
  active: { carb: 0.9, insulin: 1.15 },
  sports: { carb: 0.75, insulin: 1.35 },
};

function sigmoid(x: number): number {
  return 1 / (1 + Math.exp(-x));
}

/** Small deterministic PRNG (mulberry32). */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface SimOptions {
  /** hours of curve AFTER the meal */
  hours?: number;
  stepMinutes?: number;
}

/**
 * Simulate the battle curve. t=0 is the meal; the curve starts at t=-30 so
 * pre-bolus action is visible coming INTO the meal.
 */
export function simulateMeal(
  sim: MealSimulation,
  opts: SimOptions = {},
): GlucosePoint[] {
  const hours = opts.hours ?? 4;
  const step = opts.stepMinutes ?? 5;
  const activity = ACTIVITY_MODS[sim.activity ?? 'rest'];
  const mystery = Math.min(Math.max(sim.mystery ?? 0, 0), 1);
  const rand = mulberry32(sim.seed ?? 1337);
  const phaseA = rand() * Math.PI * 2;
  const phaseB = rand() * Math.PI * 2;

  const totalCarbRise = sim.carbsGrams * CARB_RISE_PER_G * activity.carb;
  const carbPeak = CARB_PEAK_MIN + sim.fatLevel * FAT_PEAK_SHIFT_MIN;
  const totalInsulinDrop = sim.insulinUnits * INSULIN_DROP_PER_U * activity.insulin;
  const insulinDoseT = -sim.preBolusMinutes; // t of dose relative to meal
  const insulinCenter = insulinDoseT + INSULIN_PEAK_LAG_MIN;

  const points: GlucosePoint[] = [];
  for (let t = -30; t <= hours * 60; t += step) {
    const carbs = totalCarbRise * sigmoid((t - carbPeak) / CARB_WIDTH_MIN);
    const insulin =
      sim.insulinUnits > 0
        ? totalInsulinDrop * sigmoid((t - insulinCenter) / INSULIN_WIDTH_MIN)
        : 0;
    const noise =
      mystery *
      18 *
      (0.6 * Math.sin((t / 180) * Math.PI * 2 + phaseA) +
        0.4 * Math.sin((t / 55) * Math.PI * 2 + phaseB));
    const g = sim.startGlucoseMgdl + carbs - insulin + noise;
    points.push({
      tMinutes: t,
      glucoseMgdl: Math.min(Math.max(g, MIN_CLAMPMGDL), MAX_CLAMP_MGDL),
    });
  }
  return points;
}

export function glucoseAt(points: GlucosePoint[], tMinutes: number): number {
  let best = points[0];
  let bestDist = Infinity;
  for (const p of points) {
    const d = Math.abs(p.tMinutes - tMinutes);
    if (d < bestDist) {
      bestDist = d;
      best = p;
    }
  }
  return best.glucoseMgdl;
}

export function minGlucose(points: GlucosePoint[]): number {
  return Math.min(...points.map((p) => p.glucoseMgdl));
}

/** CGM-style trend arrow over a look-back window. Teaches trend reading. */
export function trendArrow(points: GlucosePoint[], atMinute: number, windowMin = 15): string {
  const now = glucoseAt(points, atMinute);
  const before = glucoseAt(points, atMinute - windowMin);
  const delta = now - before;
  if (delta > 10) return '↑';
  if (delta > 5) return '↗';
  if (delta > -5) return '→';
  if (delta > -10) return '↘';
  return '↓';
}
